'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { CheckCircle, WarningCircle, Users, ArrowRight } from '@phosphor-icons/react';
import { teamApi } from '../api/team';
import { useAuthStore } from '../stores/authStore';
import { useTeamStore } from '../stores/teamStore';
import { Footer } from './components/Footer';

interface InviteInfo {
  team: {
    id: string;
    name: string;
  };
  role: string;
  invitedBy: string;
  expiresAt: string;
}

export function TeamInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { isAuthenticated, user } = useAuthStore();
  const { setCurrentTeamId } = useTeamStore();

  // 支持两种格式: /invite/:token 或 /invite?token=xxx
  const token = params.token || searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid' | 'accepted' | 'error'>('loading');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);

  // 验证邀请令牌
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setErrorMessage(t('teamInvite.invalidToken'));
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await teamApi.getInvitationInfo(token);
        if (response.code === 0 && response.data) {
          setInviteInfo(response.data);
          setStatus('valid');
        } else {
          setStatus('invalid');
          setErrorMessage(response.message || t('teamInvite.invalidToken'));
        }
      } catch (err: unknown) {
        // 检查是否是未授权错误（用户未登录）
        const error = err as { 
          response?: { 
            status?: number; 
            data?: { code?: number; message?: string } 
          };
          message?: string;
        };
        
        const statusCode = error.response?.status;
        const errorCode = error.response?.data?.code;
        const errorMessage = error.response?.data?.message || error.message || '';
        
        // 判断是否为未登录情况
        const isUnauthorized = statusCode === 401 || 
                              errorCode === 401 ||
                              errorCode === 1001 ||
                              errorCode === 1002 || // token过期
                              errorMessage?.includes('登录') ||
                              errorMessage?.includes('login') ||
                              errorMessage?.includes('token') ||
                              errorMessage?.includes('认证') ||
                              errorMessage?.includes('auth');
        
        if (isUnauthorized) {
          // 保存邀请信息到localStorage，登录后使用
          localStorage.setItem('pending_invite_token', token);
          // 跳转到登录页面
          navigate(`/login?invite_token=${token}`);
          return;
        }
        
        setStatus('invalid');
        setErrorMessage(t('teamInvite.invalidToken'));
      }
    };

    verifyToken();
  }, [token, t, navigate]);

  // 接受邀请
  const handleAcceptInvite = async () => {
    if (!token) return;

    setIsAccepting(true);
    try {
      const response = await teamApi.acceptInvitation(token);
      if (response.code === 0) {
        setStatus('accepted');
        // 如果已登录，更新当前团队
        if (isAuthenticated && response.data?.teamId) {
          setCurrentTeamId(response.data.teamId);
        }
      } else {
        setStatus('error');
        setErrorMessage(response.message || t('teamInvite.acceptFailed'));
      }
    } catch (err: unknown) {
      // 检查是否是未授权错误（用户未登录或token过期）
      const error = err as { response?: { status?: number; data?: { code?: number; message?: string } } };
      const isUnauthorized = error.response?.status === 401 || 
                            error.response?.data?.code === 401 ||
                            error.response?.data?.code === 1001;
      
      if (isUnauthorized) {
        // 保存邀请信息到localStorage，登录后使用
        localStorage.setItem('pending_invite_token', token);
        // 跳转到登录页面
        navigate(`/login?invite_token=${token}`);
        return;
      }
      
      setStatus('error');
      setErrorMessage(t('teamInvite.acceptFailed'));
    } finally {
      setIsAccepting(false);
    }
  };

  // 跳转到登录页
  const handleGoToLogin = useCallback(() => {
    // 保存邀请信息到localStorage，登录后使用
    localStorage.setItem('pending_invite_token', token ?? '');
    navigate(`/login?invite_token=${token}`);
  }, [token, navigate]);



  // 跳转到首页
  const handleGoToHome = () => {
    navigate('/home');
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'admin':
        return t('team.roleAdmin');
      case 'member':
      default:
        return t('team.roleMember');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <img src="/logo.svg" alt="Logo" className="w-6 h-6" />
            </div>
            <span className="text-lg font-bold text-zinc-900">{t('app.name')}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card variant="elevated">
            <CardContent className="p-8">
              {status === 'loading' && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-zinc-500">{t('teamInvite.verifying')}</p>
                </div>
              )}

              {status === 'invalid' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <WarningCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-zinc-900 mb-2">
                    {t('teamInvite.invalidTitle')}
                  </h2>
                  <p className="text-zinc-500 mb-6">{errorMessage}</p>
                  <Button onClick={handleGoToHome} variant="primary" className="w-full">
                    {t('teamInvite.goToHome')}
                  </Button>
                </div>
              )}

              {status === 'valid' && inviteInfo && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-indigo-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-zinc-900 mb-2">
                    {t('teamInvite.title')}
                  </h2>
                  <p className="text-zinc-500 mb-6">
                    {t('teamInvite.description', {
                      inviter: inviteInfo.invitedBy,
                      team: inviteInfo.team.name,
                      role: getRoleDisplay(inviteInfo.role),
                    })}
                  </p>

                  <div className="bg-zinc-50 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-indigo-500" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-zinc-900">{inviteInfo.team.name}</p>
                        <p className="text-sm text-zinc-500">
                          {t('teamInvite.role')}: {getRoleDisplay(inviteInfo.role)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {isAuthenticated ? (
                    // 已登录用户
                    <div className="space-y-3">
                      <p className="text-sm text-zinc-500 mb-4">
                        {t('teamInvite.loggedInAs', { email: user?.email })}
                      </p>
                      <Button
                        onClick={handleAcceptInvite}
                        variant="primary"
                        className="w-full"
                        isLoading={isAccepting}
                        rightIcon={<ArrowRight className="w-5 h-5" />}
                      >
                        {t('teamInvite.acceptAndJoin')}
                      </Button>
                      <Button
                        onClick={handleGoToHome}
                        variant="secondary"
                        className="w-full"
                      >
                        {t('teamInvite.goToHome')}
                      </Button>
                    </div>
                  ) : (
                    // 未登录用户
                    <div className="space-y-3">
                      <p className="text-sm text-zinc-500 mb-4">
                        {t('teamInvite.needLogin')}
                      </p>
                      <Button
                        onClick={handleGoToLogin}
                        variant="primary"
                        className="w-full"
                      >
                        {t('teamInvite.loginAndJoin')}
                      </Button>
                      {/* <Button
                        onClick={handleGoToRegister}
                        variant="secondary"
                        className="w-full"
                      >
                        {t('teamInvite.registerAndJoin')}
                      </Button> */}
                    </div>
                  )}
                </div>
              )}

              {status === 'accepted' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-zinc-900 mb-2">
                    {t('teamInvite.acceptedTitle')}
                  </h2>
                  <p className="text-zinc-500 mb-6">
                    {t('teamInvite.acceptedDescription', { team: inviteInfo?.team.name })}
                  </p>
                  <Button onClick={handleGoToHome} variant="primary" className="w-full">
                    {t('teamInvite.goToHome')}
                  </Button>
                </div>
              )}

              {status === 'error' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <WarningCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-zinc-900 mb-2">
                    {t('teamInvite.errorTitle')}
                  </h2>
                  <p className="text-zinc-500 mb-6">{errorMessage}</p>
                  <Button onClick={handleGoToHome} variant="primary" className="w-full">
                    {t('teamInvite.goToHome')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
